/** Baker Ripley operations timezone (Houston, TX). */
export const HOUSTON_TIME_ZONE = 'America/Chicago';

export class FormDateUtils {
  /**
   * Calendar Y/M/D in Houston for the given instant (defaults to now).
   * Month is 1–12.
   */
  static getHoustonCalendarParts(date: Date = new Date()): { year: number; month: number; day: number } {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: HOUSTON_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);

    const get = (type: Intl.DateTimeFormatPartTypes): number =>
      Number(parts.find((part) => part.type === type)?.value);

    return {
      year: get('year'),
      month: get('month'),
      day: get('day'),
    };
  }

  /**
   * Local Date at midnight representing Houston's current calendar day.
   * Safe for building weekday sequences without using the browser timezone.
   */
  static getHoustonTodayDate(now: Date = new Date()): Date {
    const { year, month, day } = FormDateUtils.getHoustonCalendarParts(now);
    return new Date(year, month - 1, day);
  }

  /** Houston calendar day as YYYY-MM-DD. */
  static formatHoustonIsoDate(now: Date = new Date()): string {
    const { year, month, day } = FormDateUtils.getHoustonCalendarParts(now);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  /** Display like "Monday 18, October" using Houston's calendar day. */
  static formatHoustonLongDate(now: Date = new Date(), locale: string = 'en-US'): string {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone: HOUSTON_TIME_ZONE,
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).formatToParts(now);

    const get = (type: Intl.DateTimeFormatPartTypes): string =>
      parts.find((part) => part.type === type)?.value ?? '';

    return `${get('weekday')} ${get('day')}, ${get('month')}`;
  }

  /**
   * Parse an API date string (ISO or YYYY-MM-DD) into a local Date at midnight.
   * Use this when displaying dates from the API to avoid timezone shifting the day
   * (e.g. "2009-03-04T08:00:00Z" should display as March 4, not March 3).
   */
  static parseDateOnly(value: string | null | undefined): Date | null {
    if (value == null || value === '') return null;
    const s = String(value).trim();
    const match = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (!match) return null;
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    const d = new Date(year, month, day);
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * Format a Date as YYYY-MM-DD using local date parts (no timezone conversion).
   * Use this when sending dates to the API so the stored date is not shifted.
   */
  static formatDate(date: Date | string, format: string = 'YYYY-MM-DD'): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN((d as Date).getTime())) return '';

    const year = (d as Date).getFullYear();
    const month = String((d as Date).getMonth() + 1).padStart(2, '0');
    const day = String((d as Date).getDate()).padStart(2, '0');

    return format
      .replace('YYYY', String(year))
      .replace('MM', month)
      .replace('DD', day);
  }

  static isToday(date: Date | string): boolean {
    const d = typeof date === 'string' ? FormDateUtils.parseDateOnly(date) ?? new Date(date) : date;
    if (isNaN(d.getTime())) return false;
    const houstonToday = FormDateUtils.getHoustonTodayDate();
    return (
      d.getFullYear() === houstonToday.getFullYear() &&
      d.getMonth() === houstonToday.getMonth() &&
      d.getDate() === houstonToday.getDate()
    );
  }

  static addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }
}

